// ProductionList.js
import React, { useEffect, useState, useRef } from 'react';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { format } from 'date-fns';
import { Calendar } from 'primereact/calendar';
import { OrderService } from './service/OrderService';
import { add, isWithinInterval } from 'date-fns';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { ScrollPanel } from 'primereact/scrollpanel';

import {
  formatDateLong,
  formatDateWeekday,
  formatTime,
  formatDayAndDate,
} from '../utils.js';

const ProductionList = () => {
  const [orders, setOrders] = useState([]);
  const [ordersByDate, setOrdersByDate] = useState({});
  const [sortedDates, setSortedDates] = useState([]);
  const [showCheckBox, setShowCheckBox] = useState(false);
  const [showExtraDetails, setShowExtraDetails] = useState(true);

  const [isResyncing, setIsResyncing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const toast = useRef(null);

  const defaultDateRange = {
    start: new Date().toISOString(),
    end: add(new Date(), { days: 90 }).toISOString(),
  };

  const storedDateRange = localStorage.getItem('dateRange_');
  const initialDateRange = storedDateRange
    ? JSON.parse(storedDateRange)
    : defaultDateRange;

  const [dateRange, setDateRange] = useState({
    start: new Date(initialDateRange.start),
    end: new Date(initialDateRange.end),
  });

  useEffect(() => {
    const storedDateRange = {
      start: dateRange.start.toISOString(),
      end: dateRange.end.toISOString(),
    };
    localStorage.setItem('dateRange_', JSON.stringify(storedDateRange));
  }, [dateRange]);

  useEffect(() => {
    const fetchOrders = async () => {
      setIsRefreshing(true);
      try {
        const data = await OrderService.getOrdersByDateRange(dateRange.start, dateRange.end);
        // Filter out canceled invoices and ensure dates are within range
        const filteredData = data.filter(order => {
          const orderDate = new Date(order.dueDate);
          return isWithinInterval(orderDate, { start: dateRange.start, end: dateRange.end }) && order.invoiceStatus !== 'CANCELED';
        });

        // Sort orders by Order ID if available, else by dueDate
        filteredData.sort((a, b) => {
          if (a.orderID && b.orderID) {
            return a.orderID.localeCompare(b.orderID);
          } else if (a.orderID) {
            return -1;
          } else if (b.orderID) {
            return 1;
          } else {
            return new Date(a.dueDate) - new Date(b.dueDate);
          }
        });

        setOrders(filteredData);

        // Group the orders by date
        const groupedOrders = filteredData.reduce((groups, order) => {
          // Parse the date and format to the correct timezone (America/Chicago)
          const orderDate = new Date(order.dueDate);
          const localDate = orderDate.toLocaleDateString('en-US', { timeZone: 'America/Chicago' });
          if (!groups[localDate]) {
            groups[localDate] = [];
          }
          groups[localDate].push(order);
          return groups;
        }, {});
        setOrdersByDate(groupedOrders);

        // Sort the keys (the dates) in ascending order initially
        setSortedDates(Object.keys(groupedOrders).sort((a, b) => new Date(a) - new Date(b)));
      } catch (error) {
        console.error("Error fetching orders:", error);
        toast.current.show({ severity: 'error', summary: 'Error', detail: `Failed to fetch orders: ${error.message}` });
      } finally {
        setIsRefreshing(false);
      }
    };

    fetchOrders();
  }, [dateRange]);

  const sortDatesAsc = () => {
    setSortedDates([...sortedDates].sort((a, b) => new Date(a) - new Date(b)));
  };

  const sortDatesDesc = () => {
    setSortedDates([...sortedDates].sort((a, b) => new Date(b) - new Date(a)));
  };

  const aggregateItemsByFlavor = (items) => {
    return items.reduce((groups, item) => {
      if (!groups[item.flavor]) {
        groups[item.flavor] = 0;
      }
      groups[item.flavor] += item.quantity;
      return groups;
    }, {});
  };

  const aggregateItemsByItem = (items) => {
    return items.reduce((groups, item) => {
      if (!groups[item.item]) {
        groups[item.item] = 0;
      }
      groups[item.item] += item.quantity;
      return groups;
    }, {});
  };

  const handlePrint = () => {
    window.print();
  };

  const OrderNote = ({ note }) => (
    note && note.trim() !== '' && (
      <div>
        <strong>Order Note:</strong>
        {note}
      </div>
    )
  );

  const FlavorQuantityList = ({ items, itemLabel }) => (
    Object.entries(aggregateItemsByFlavor(items)).map(([flavor, quantity]) => (
      <p key={flavor}>
        {showCheckBox && <Checkbox style={{ marginRight: '5px' }} />}
        {quantity} {flavor} {items.length > 0 && items[0].type ? items[0].type.toLowerCase() : itemLabel}
      </p>
    ))
  );

  const aggregateGeneralItemsByItemAndFlavor = (items) => {
    return items.reduce((groups, item) => {
      const key = item.item + "-" + item.flavor; // Combined key.
      if (!groups[key]) {
        groups[key] = { quantity: 0, flavor: item.flavor, description: item.description, flavorNotes: item.flavorNotes };
      }
      groups[key].quantity += item.quantity;
      return groups;
    }, {});
  };

  const GeneralItemQuantityList = ({ items }) => (
    Object.entries(aggregateGeneralItemsByItemAndFlavor(items)).map(([key, data]) => (
      <p key={key}>
        {showCheckBox && <Checkbox style={{ marginRight: '5px' }} />}
        {data.quantity} {data.flavor} {data.flavorNotes ? data.flavorNotes + ' - ' : ''} {key.split('-')[0]} {data.description}
      </p>
    ))
  );

  
  return (
    <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <Toast ref={toast} />
      <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Button label="Sort Asc" onClick={sortDatesAsc} />
        <Button label="Sort Desc" onClick={sortDatesDesc} />
        <Calendar value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.value })} />
        <Calendar value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.value })} />
        <Button label="Print" onClick={handlePrint} />
        <Button label="Show Checkboxes" onClick={() => setShowCheckBox(!showCheckBox)} />
        <Button label={showExtraDetails ? "Hide Extra Details" : "Show Extra Details"} onClick={() => setShowExtraDetails(!showExtraDetails)} />
        {/* <Button
          label="Resync Invoices"
          onClick={resyncInvoices}
          icon="pi pi-refresh"
          loading={isResyncing}
          disabled={isResyncing || isRefreshing}
        /> */}
      </div>
      <ScrollPanel style={{ height: 'calc(100vh - 125px)' }}>
        {sortedDates.map((date, index) => (
          <Panel header={formatDayAndDate(date)} key={index}>
            {ordersByDate[date].map((order, orderIndex) => (
              <div key={orderIndex} style={{ color: (order.invoiceStatus !== 'PAID' && order.invoiceStatus !== 'COMP' && order.invoiceStatus !== 'PARTIALLY_PAID' && order.invoiceStatus !== 'ONLINE') ? 'red' : 'inherit' }}>
                <p>
                  <strong>
                    <u>
                      {showCheckBox && <Checkbox style={{ marginRight: '5px' }} />}
                      {order.orderID ? `${order.orderID}: ` : ''} {order.customer.name} - {order.name} - {order.type} - {formatTime(order.scheduledTime)}
                    </u>
                    {showExtraDetails && (
                      <span style={{
                        color: (order.invoiceStatus === 'PAID' || order.invoiceStatus === 'COMP') ? 'white' : 'black',
                        backgroundColor: (order.invoiceStatus === 'PAID' || order.invoiceStatus === 'COMP') ? 'green' : 'yellow',
                        padding: '2px 4px',
                        marginLeft: '8px',
                        borderRadius: '4px',
                      }}>
                        {order.invoiceStatus}
                      </span>
                    )}
                  </strong>
                </p>
                {order.orderItems.map((orderItem, index) => (
                  <div key={index}>
                    {orderItem.cakes.map((cake, cakeIndex) => (
                      <div key={cakeIndex}>
                        {orderItem.cakes.length > 1 && (<><strong>Cake {cakeIndex + 1}:</strong> {cake.description}<br /></>)}
                        {cake.tiers.map((tier, tierIndex) => (
                          <p key={tierIndex}>
                            {showCheckBox && <Checkbox style={{ marginRight: '5px' }} />}
                            {tier.size}"{' '}
                            {`(${tier.layers}${(tier.layers === 2 && (tier.size < 6 || tier.size === '6' || tier.size === '4')) ? ' layers - Smash)' : ' layers)'}`}{' '}
                            {tier.shape} {tier.flavor} {tier.soak === '' || !tier.soak ? '' : `w/ ${tier.soak} soak,`} w/ {tier.filling} {tier.premiumFilling && 'w/'} {tier.premiumFilling}{' '}
                          </p>
                        ))}
                        {cake.toppers.length > 0 && (
                          <div>
                            <strong>Topper:</strong>{' '}
                            {cake.toppers.map((topper, topperIndex) => (
                              <span key={topperIndex}>
                                {showCheckBox && <Checkbox style={{ marginRight: '5px' }} />}
                                {topper.description}
                                {topperIndex !== cake.toppers.length - 1 && ', '}
                              </span>
                            ))}
                          </div>
                        )}
                        {cake.decoratingItems.length > 0 && (
                          <div>
                            <strong>Decorating Items:</strong>{' '}
                            {cake.decoratingItems.map((decoratingItem, decoratingIndex) => (
                              <span key={decoratingIndex}>
                                {showCheckBox && <Checkbox style={{ marginRight: '5px' }} />}
                                {decoratingItem.description}
                                {decoratingIndex !== cake.decoratingItems.length - 1 && ', '}
                              </span>
                            ))}
                          </div>
                        )}
                        {cake.bakingNotes && cake.bakingNotes.trim() !== '' && (
                          <div>
                            <strong>Baking Note:</strong> {cake.bakingNotes}
                          </div>
                        )}
                        {cake.crumbCoatingNotes && cake.crumbCoatingNotes.trim() !== '' && (
                          <div>
                            <strong>Crumb Coat Note:</strong> {cake.crumbCoatingNotes}
                          </div>
                        )}
                        {cake.decoratingNotes && cake.decoratingNotes.trim() !== '' && (
                          <div>
                            <strong>Decorating Note:</strong> {cake.decoratingNotes}
                          </div>
                        )}
                        {cake.cakeBoardInscription && cake.cakeBoardInscription.trim() !== '' && (
                          <div>
                            <strong>Cake Board Inscription:</strong> {cake.cakeBoardInscription}
                          </div>
                        )}
                        {cake.cakeBoardNote && cake.cakeBoardNote.trim() !== '' && (
                          <div>
                            <strong>Cake Board Note:</strong> {cake.cakeBoardNote}
                          </div>
                        )}
                        {cake.specialInstructions && cake.specialInstructions.trim() !== '' && (
                          <div>
                            <strong>Special Instructions:</strong> {cake.specialInstructions}
                          </div>
                        )}
                        {orderItem.cakes.length > 1 && (<div style={{ marginBottom: '5px' }} />)}
                      </div>
                    ))}
                    <FlavorQuantityList items={orderItem.cookies} itemLabel="cookies" />
                    <FlavorQuantityList items={orderItem.cupcakes} itemLabel="cupcakes" />
                    <FlavorQuantityList items={orderItem.cakePops} itemLabel="cake pops" />
                    <GeneralItemQuantityList items={orderItem.generalItems} />
                    <OrderNote note={order.orderNote} />
                  </div>
                ))}
                <br />
              </div>
            ))}
          </Panel>
        ))}
      </ScrollPanel>
    </div>
  );
};

export default ProductionList;